import React, { useEffect, useState } from "react";
import { subscribeToasts, dismissToast } from "./toast.js";

const AUTO_DISMISS_MS = 4500;
const FADE_MS = 300;

const TYPE_STYLES = {
  success: { bg: "var(--toast-success-bg)", border: "var(--toast-success-border)", icon: "✓", iconColor: "var(--toast-success-icon)" },
  error: { bg: "var(--toast-error-bg)", border: "var(--toast-error-border)", icon: "✕", iconColor: "var(--toast-error-icon)" },
  warning: { bg: "var(--toast-warning-bg)", border: "var(--toast-warning-border)", icon: "⚠", iconColor: "var(--toast-warning-icon)" },
  info: { bg: "var(--toast-info-bg)", border: "var(--toast-info-border)", icon: "ℹ", iconColor: "var(--toast-info-icon)" },
};

// One toast's own lifecycle (its fade-in/fade-out) lives here, separate
// from the shared list in toast.js — that's what lets it visually fade
// out over FADE_MS before it actually disappears from the list, rather
// than just vanishing instantly.
function ToastItem({ toast }) {
  const [closing, setClosing] = useState(false);
  const style = TYPE_STYLES[toast.type] || TYPE_STYLES.success;

  const close = () => {
    setClosing(true);
    setTimeout(() => dismissToast(toast.id), FADE_MS);
  };

  // LONGER FOR A LONGER MESSAGE.
  //
  // 4.5 seconds suits "Saved." and is far too short for anything an
  // operator has to read and act on — the competition-clash warning ran
  // to three lines and vanished before it could be read (4 Sep).
  //
  // Roughly 22 characters a second. That is slower than reading speed on
  // purpose: these messages are read once, cold, in the middle of doing
  // something else, and the operator has to take an action from them.
  //
  // The old 4.5s is the floor, so "Saved." is unchanged, and 20s the
  // ceiling so nothing sits there indefinitely. Any toast can be
  // dismissed by hand.
  const dismissAfterMs = Math.min(
    20000,
    Math.max(AUTO_DISMISS_MS, Math.round((String(toast.message || "").length / 22) * 1000))
  );

  useEffect(() => {
    const timer = setTimeout(close, dismissAfterMs);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "12px 14px",
        background: style.bg,
        border: `1px solid ${style.border}`,
        borderRadius: 8,
        color: "var(--text-primary)",
        fontSize: 14,
        minWidth: 260,
        maxWidth: 360,
        boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
        opacity: closing ? 0 : 1,
        transform: closing ? "translateY(8px)" : "translateY(0)",
        transition: `opacity ${FADE_MS}ms ease, transform ${FADE_MS}ms ease`,
      }}
    >
      <span style={{ color: style.iconColor, fontSize: 16, fontWeight: 700, flexShrink: 0 }}>{style.icon}</span>
      <span style={{ flex: 1 }}>{toast.message}</span>
      <button
        onClick={close}
        aria-label="Dismiss"
        style={{
          background: "none",
          border: "none",
          color: "var(--text-secondary)",
          cursor: "pointer",
          fontSize: 16,
          lineHeight: 1,
          padding: 0,
          flexShrink: 0,
        }}
      >
        ×
      </button>
    </div>
  );
}

// Mounted once (in main.jsx's Root), so it's available on every page
// regardless of which one is currently active.
export default function ToastContainer() {
  const [toasts, setToasts] = useState([]);

  useEffect(() => subscribeToasts(setToasts), []);

  if (toasts.length === 0) return null;

  return (
    <div
      style={{
        position: "fixed",
        bottom: 20,
        right: 20,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        zIndex: 1000,
      }}
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} />
      ))}
    </div>
  );
}
